import { useMoney } from '../currency';
import { useT } from '../i18n';

/**
 * CENA ZA MĚRNOU JEDNOTKU (#9) — `182,50 Kč/kg`.
 *
 * Server ji počítá v `apps/api/src/pricing/unit-price.ts` z NÁZVU produktu
 * a posílá `null` všude, kde si není jistý. Výloha proto nemá co dopočítávat
 * ani opravovat: buď číslo dorazí, nebo se prvek nevykreslí.
 *
 * JE TO JEN ZOBRAZENÍ. Do košíku, do součtu ani do pokladny se tahle hodnota
 * nikdy nedostane — závazná je cena varianty, kterou počítá server.
 *
 * Typy `ShopVariant` a `ShopListItem` v `../api` pole zatím nenesou (ten
 * soubor patří jinam), proto se čte STRUKTURÁLNĚ — stejným způsobem, jakým
 * si `ProductPage` bere min/max objednatelné množství. Jakmile se typ
 * doplní, tenhle soubor se měnit nemusí.
 */

export type ContentUnit = 'kg' | 'l';

export interface UnitPrice {
  readonly unit: ContentUnit;
  /** Cena za 1 kg / 1 l v halířích CZK, tak jak chodí ze serveru. */
  readonly pricePerUnitMinor: number;
}

/**
 * Přečte `unitPrice` z varianty nebo z položky výpisu. Cokoli jiného než
 * očekávaný tvar je `null` — rozbité pole nesmí shodit stránku ani ukázat
 * `NaN Kč/kg`.
 */
export function readUnitPrice(source: unknown): UnitPrice | null {
  if (typeof source !== 'object' || source === null) return null;
  const raw = (source as { unitPrice?: unknown }).unitPrice;
  if (typeof raw !== 'object' || raw === null) return null;

  const { unit, pricePerUnitMinor } = raw as {
    unit?: unknown;
    pricePerUnitMinor?: unknown;
  };
  if (unit !== 'kg' && unit !== 'l') return null;
  if (typeof pricePerUnitMinor !== 'number') return null;
  if (!Number.isFinite(pricePerUnitMinor) || pricePerUnitMinor <= 0) return null;

  return { unit, pricePerUnitMinor };
}

/**
 * Vykreslí cenu za měrnou jednotku, nebo nic.
 *
 * Částka jde přes `useMoney().format`, takže v cizí zobrazovací měně sedí
 * s cenou nad ní (`4,50 €/kg`) a nezůstane osamocená v korunách.
 */
export function UnitPriceNote({ source }: { source: unknown }) {
  const { format } = useMoney();
  const { t } = useT();

  const unitPrice = readUnitPrice(source);
  if (unitPrice === null) return null;

  const price = format(unitPrice.pricePerUnitMinor);
  const isKg = unitPrice.unit === 'kg';

  return (
    <span
      className="unit-price muted small"
      title={t(isKg ? 'unit.titleKg' : 'unit.titleL')}
    >
      {t(isKg ? 'unit.perKg' : 'unit.perL', { price })}
    </span>
  );
}
